import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { BULK_SELECTION_INITIAL_STATE } from 'src/app/Orders/order.const';
import BulkActionBar from 'src/components/BulkActionBar';
import CardCmp from 'src/components/CardCmp';
import CheckBox from 'src/components/CheckBox';
import TableCmp from 'src/components/TableCmp';
import { orderDetailsData, orderDetailsLoading, selectCurrentLegData, selectOrderLegActiveTab } from 'src/redux/orders.slice';

import LineItemRows from './LineItemRows';
import LineItemRowsLoader from './LineItemRowsLoader';

const LineItems = () => {
    const { t } = useTranslation();
    const loading = useSelector(orderDetailsLoading);
    const order = useSelector(orderDetailsData);
    const currentLegData = useSelector(selectCurrentLegData);
    const orderLegActiveTab = useSelector(selectOrderLegActiveTab);
    // Master tab shows ALL of the order's line items; a leg tab shows only the items
    // on board during that leg (backend scopes leg.order_dimensions by pickup→drop span).
    const orderDimensionData: any[] = (orderLegActiveTab?.isMaster ? order?.order_dimensions : currentLegData?.order_dimensions) ?? [];

    const [bulkSelection, setBulkSelection] = useState<{ isOpen: boolean; data: number[] }>(BULK_SELECTION_INITIAL_STATE);
    const [showAllSelected, setShowAllSelected] = useState(false);
    const selectedIds = bulkSelection.data;

    const setSelectedIds = useCallback((ids: number[]) => {
        if (ids.length === 0) setShowAllSelected(false);
        setBulkSelection({ isOpen: ids.length > 0, data: ids });
    }, []);

    const handleToggleSelectRow = useCallback((id: number) => {
        setBulkSelection(({ data }) => {
            const next = data.includes(id) ? data.filter((i) => i !== id) : [...data, id];
            if (next.length === 0) setShowAllSelected(false);

            return { isOpen: next.length > 0, data: next };
        });
    }, []);

    const allIds = useMemo(() => orderDimensionData.map((item: any) => item.id), [orderDimensionData]);

    const displayData = useMemo(
        () => (showAllSelected ? orderDimensionData.filter((item: any) => selectedIds.includes(item.id)) : orderDimensionData),
        [showAllSelected, orderDimensionData, selectedIds]
    );

    const headCells = useMemo(
        () => [
            {
                id: 'checkbox',
                name: (
                    <CheckBox
                        id="selectAllLineItems"
                        checked={displayData.length > 0 && displayData.every((item: any) => selectedIds.includes(item.id))}
                        onChangeFunc={() => {
                            const pageIds = displayData.map((item: any) => item.id);
                            const allSelected = pageIds.every((id: number) => selectedIds.includes(id));
                            setSelectedIds(allSelected ? selectedIds.filter((id) => !pageIds.includes(id)) : Array.from(new Set([...selectedIds, ...pageIds])));
                        }}
                        name="selectAllLineItems"
                        value="selectAllLineItems"
                        disabled={loading}
                    />
                ),
                sortable: false,
                visible: true,
                rowClassName: '!pr-2 w-[40px] min-w-[40px] max-w-[40px] sticky left-0 bg-inherit z-[1]',
            },
            { id: 'quantity', name: t('Common.Quantity'), rowClassName: '' },
            { id: 'handling_unit', name: t('Common.Handling_unit'), rowClassName: '' },
            {
                id: 'dimensions_in',
                name: `${t('Common.Dimensions')} (${orderDimensionData?.[0]?.lengthMeasurement === 'CM' ? 'cm' : 'In'})`,
                rowClassName: '',
            },
            { id: 'freight_class', name: t('Orders.Freight_Class'), rowClassName: '' },
            {
                id: 'unit_weight_lbs',
                name: `${t('Orders.Unit_Weight')} (${orderDimensionData?.[0]?.weightMeasurement === 'KGS' ? 'kgs' : 'lbs'})`,
                rowClassName: '',
            },
            {
                id: 'total_weight_lbs',
                name: `Total Weight (${orderDimensionData?.[0]?.weightMeasurement === 'KGS' ? 'kgs' : 'lbs'})`,
                rowClassName: '',
            },
            { id: 'ref_number', name: t('Orders.REF_Number'), rowClassName: '' },
            { id: 'commodity', name: t('Common.Commodity'), rowClassName: '' },
        ],
        [orderDimensionData, t, displayData, selectedIds, loading, setSelectedIds]
    );

    if (!loading && !orderDimensionData.length) return null;

    return (
        <CardCmp headerTitle={t('Orders.Line_Items')} headerParentClassName="!rounded-t-lg" isCardContentWithoutSpacing={true} cardClassName="mt-5">
            <div className="w-full table-box-container">
                <div className="w-full table-box-container relative">
                    {bulkSelection.isOpen && (
                        <BulkActionBar selectedRows={selectedIds} setSelectedRows={setSelectedIds} allIds={allIds} showOnlySelected={showAllSelected} onShowOnlySelectedChange={setShowAllSelected} />
                    )}
                    <div className="overflow-x-auto custom-scrollbar scrollbar-hide">
                        <TableCmp
                            tableRowHeight="xs"
                            headCells={headCells}
                            tableDataArr={loading ? [] : displayData}
                            TableRowCmp={LineItemRows}
                            TableLoaderRowCmp={LineItemRowsLoader}
                            isTableDataLoading={loading}
                            tableBodyRowClass="last:border-b-0"
                            numberOfSkeletonRows={3}
                            isBulkBarOpen={bulkSelection.isOpen}
                            isFirstColumnSticky
                            tableRowCmpProps={{
                                selectedIds,
                                onToggleSelect: handleToggleSelectRow,
                            }}
                        />
                    </div>
                </div>
            </div>
        </CardCmp>
    );
};

export default LineItems;
